// Life & Wealth Solutions - Whale Opportunities Component
// Shows policies >$15k premium ready for Agent 12 approval
//
// FIXED: The production-mode GET call read response.data.whales, but the
// real backend endpoint (GET /agent12/whale-opportunities/{agency_id})
// has always returned the list under response.data.opportunities — so
// this panel showed zero whales in production regardless of how many
// were actually pending. It also expected fields (client_location,
// total_premium, agent_9_notes, agent_11_status, a 3-flag
// compliance_checks object) that do not exist on the real PolicyResponse
// schema at all — meaning even a corrected field name would have crashed
// on render. Real fields are now mapped explicitly below; where the
// schema does not yet track a granular signal (e.g. three separate
// compliance booleans), that is stated honestly in the mapping rather
// than invented.

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { isDemoMode, mockData } from '../../services/mockDataService';
import { useAuth } from '../../hooks/useAuth';
import PolicyDetailsModal from './PolicyDetailsModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
const WHALE_THRESHOLD = 15000;

interface WhalePolicy {
  policy_id: string;
  client_name: string;
  client_location: string; // mapped from real `state` field
  total_premium: number;   // mapped from real `premium_amount`
  commission_value: number;
  policy_type: string;
  agent_9_notes: string;   // real, generated from real premium/threshold values — not narrative fiction
  agent_11_status: 'approved' | 'pending' | 'flagged';
  compliance_checks: {
    state_vetted: boolean;
    carrier_approved: boolean;
    bias_check_passed: boolean;
  };
  created_at: string;
}

interface Props {
  userId: string;
}

function mapPolicyToWhale(policy: any): WhalePolicy {
  const score: number | null = policy.compliance_score;
  // My Sentinel has run a compliance check on this policy if compliance_score is populated.
  // The schema does not yet track three independent flags (state/carrier/bias) per policy —
  // all three currently reflect that single real signal rather than three separate checks.
  const sentinelChecked = score !== null && score !== undefined;
  const status: WhalePolicy['agent_11_status'] =
    score === null || score === undefined ? 'pending' :
    score < 70 ? 'flagged' :
    score >= 90 ? 'approved' : 'pending';

  return {
    policy_id: policy.policy_id,
    client_name: policy.client_name,
    client_location: policy.state || '—',
    total_premium: Number(policy.premium_amount || 0),
    commission_value: Number(policy.commission_value || 0),
    policy_type: policy.policy_type || 'Policy',
    agent_9_notes: `Escalated by My Maestro — premium of $${Number(policy.premium_amount || 0).toLocaleString()} exceeds the $${WHALE_THRESHOLD.toLocaleString()} whale threshold.${score !== null && score !== undefined ? ` My Sentinel compliance score: ${score}/100.` : ' Awaiting My Sentinel compliance review.'}`,
    agent_11_status: status,
    compliance_checks: {
      state_vetted: sentinelChecked,
      carrier_approved: Boolean(policy.carrier),
      bias_check_passed: sentinelChecked && score >= 90,
    },
    created_at: policy.created_at,
  };
}

const WhaleOpportunities: React.FC<Props> = ({ userId }) => {
  const { user } = useAuth();
  const [whales, setWhales] = useState<WhalePolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bindingPolicy, setBindingPolicy] = useState<string | null>(null);
  const [selectedPolicy, setSelectedPolicy] = useState<WhalePolicy | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const agencyId = user?.agency_id;

  const loadWhales = useCallback(async () => {
    try {
      if (isDemoMode()) {
        const mockWhales = mockData.whales();
        setWhales(mockWhales);
        setError('');
        setLoading(false);
        return;
      }

      if (!agencyId) {
        setError('No agency associated with this account');
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_URL}/agent12/whale-opportunities/${agencyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const opportunities = response.data.opportunities || [];
      setWhales(opportunities.map(mapPolicyToWhale));
      setError('');
    } catch (err: any) {
      console.error('Failed to load whales:', err);
      setError(err.response?.data?.detail || 'Failed to load whale opportunities');
    } finally {
      setLoading(false);
    }
  }, [agencyId]);

  useEffect(() => {
    loadWhales();
  }, [loadWhales]);

  const handleOneTapBind = async (policyId: string) => {
    if (!window.confirm('Confirm: Bind this policy and provide your licensed signature?')) return;
    setBindingPolicy(policyId);

    try {
      if (isDemoMode()) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        alert('✅ Policy bound successfully! Agent 7 is notifying the client. (Demo Mode)');
        setWhales(whales.filter(w => w.policy_id !== policyId));
        return;
      }

      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API_URL}/policies/${policyId}/bind`,
        { agent_12_user_id: userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('✅ Policy bound successfully! Agent 7 is notifying the client.');
      await loadWhales();
    } catch (err: any) {
      alert(`❌ Binding failed: ${err.response?.data?.detail || 'Unknown error'}`);
    } finally {
      setBindingPolicy(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-2 text-gray-600">Loading whale opportunities...</p>
      </div>
    );
  }

  if (error) {
    return <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>;
  }

  if (whales.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">🐋</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Whales Right Now</h3>
        <p className="text-gray-600">My Maestro will alert you when high-value opportunities (&gt;$15k premium) are ready.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">
          🐋 {whales.length} High-Value Opportunit{whales.length === 1 ? 'y' : 'ies'}
        </h3>
        <button onClick={loadWhales} className="text-sm text-blue-600 hover:text-blue-800">🔄 Refresh</button>
      </div>

      {whales.map((whale) => (
        <div key={whale.policy_id} className="border border-gray-200 rounded-lg p-6 bg-gradient-to-r from-yellow-50 to-white hover:shadow-lg transition-shadow">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-2xl">🐋</span>
                <h4 className="text-xl font-bold text-gray-900">{whale.client_name}</h4>
              </div>
              <p className="text-sm text-gray-600">{whale.client_location}</p>
              <p className="text-xs text-gray-500 mt-1">Policy ID: {whale.policy_id}</p>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-600">Total Premium</div>
              <div className="text-2xl font-bold text-green-600">${whale.total_premium.toLocaleString()}</div>
              <div className="text-xs text-gray-600 mt-1">Your Commission: ${whale.commission_value.toLocaleString()}</div>
            </div>
          </div>

          <div className="mb-4">
            <span className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full">{whale.policy_type}</span>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">🛡️</span>
              <span className="font-medium text-gray-900">Compliance Shield (My Sentinel)</span>
            </div>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="flex items-center gap-2">
                <span className={whale.compliance_checks.state_vetted ? 'text-green-600' : 'text-red-600'}>
                  {whale.compliance_checks.state_vetted ? '✅' : '❌'}
                </span>
                <span className="text-gray-700">Sentinel Reviewed</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={whale.compliance_checks.carrier_approved ? 'text-green-600' : 'text-red-600'}>
                  {whale.compliance_checks.carrier_approved ? '✅' : '❌'}
                </span>
                <span className="text-gray-700">Carrier on File</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={whale.compliance_checks.bias_check_passed ? 'text-green-600' : 'text-red-600'}>
                  {whale.compliance_checks.bias_check_passed ? '✅' : '❌'}
                </span>
                <span className="text-gray-700">Score ≥ 90</span>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-2">
              <span className="text-lg">🎯</span>
              <div className="flex-1">
                <div className="font-medium text-gray-900 mb-1">My Maestro (Orchestrator) Notes:</div>
                <p className="text-sm text-gray-700">{whale.agent_9_notes}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => handleOneTapBind(whale.policy_id)}
              disabled={bindingPolicy === whale.policy_id || whale.agent_11_status === 'flagged'}
              className={`flex-1 py-3 px-6 rounded-lg font-bold text-white text-lg transition-all transform hover:scale-105 ${
                bindingPolicy === whale.policy_id ? 'bg-gray-400 cursor-wait' :
                whale.agent_11_status === 'flagged' ? 'bg-red-500 cursor-not-allowed' :
                'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 shadow-lg'
              }`}
            >
              {bindingPolicy === whale.policy_id ? 'Binding Policy...' :
               whale.agent_11_status === 'flagged' ? '🚫 COMPLIANCE HOLD - Review Required' :
               '✅ CONFIRM & BIND POLICY'}
            </button>
            <button
              onClick={() => { setSelectedPolicy(whale); setIsModalOpen(true); }}
              className="px-6 py-3 border border-blue-600 text-blue-600 rounded-lg font-medium hover:bg-blue-50 transition-colors"
            >
              📋 View Details
            </button>
          </div>

          <div className="mt-3 text-xs text-gray-500 text-right">
            Detected: {new Date(whale.created_at).toLocaleString()}
          </div>
        </div>
      ))}

      {selectedPolicy && (
        <PolicyDetailsModal
          policy={selectedPolicy}
          isOpen={isModalOpen}
          onClose={() => { setIsModalOpen(false); setSelectedPolicy(null); }}
          onBind={handleOneTapBind}
        />
      )}
    </div>
  );
};

export default WhaleOpportunities;
